
import React from 'react';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { StandardSelect } from '@/components/ui/form-fields';
import { Trees, Mountain } from 'lucide-react';
import styles from '../FilterBar.module.css';
import { motion } from 'framer-motion';

interface EnvironmentFilterProps {
  environmentOptions: FilterOption[];
  onEnvironmentChange: (options: FilterOption[]) => void;
  backgroundOptions: FilterOption[];
  onBackgroundChange: (options: FilterOption[]) => void;
  selectedBackground: string | null;
  showEnvironment?: boolean;
  showBackground?: boolean;
}

export const EnvironmentFilter: React.FC<EnvironmentFilterProps> = ({
  environmentOptions,
  onEnvironmentChange,
  backgroundOptions,
  onBackgroundChange,
  selectedBackground,
  showEnvironment = true,
  showBackground = true
}) => {
  // Handle environment select change - no clearing logic needed
  const handleEnvironmentChange = (selected: string | string[]) => {
    const value = typeof selected === 'string' ? selected : '';
    
    const updatedOptions = environmentOptions.map(option => ({
      ...option,
      checked: option.id === value
    }));
    onEnvironmentChange(updatedOptions);
  };

  // Handle background select change - no clearing logic needed
  const handleBackgroundChange = (val: string | string[]) => {
    const value = typeof val === 'string' ? val : '';
    
    const updatedOptions = backgroundOptions.map(option => ({
      ...option,
      checked: option.id === value
    }));
    onBackgroundChange(updatedOptions);
  };

  // Get selected environment option
  const selectedEnvironment = environmentOptions.find(option => option.checked)?.id || '';
  
  // Helper to convert FilterOptions to StandardSelect options format
  const mapToSelectOptions = (options: FilterOption[]) => {
    return options.map(option => ({
      value: option.id,
      label: option.label
    }));
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {showEnvironment && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <StandardSelect
            id="environment"
            label="Environment Type"
            icon={<Trees size={16} />}
            colorVariant="green"
            value={selectedEnvironment}
            isMulti={false}
            onChange={handleEnvironmentChange}
            options={[
              { value: 'none', label: 'None' },
              ...mapToSelectOptions(environmentOptions)
            ]}
            placeholder="Environment"
            className={styles.vibrantInput}
          />
        </motion.div>
      )}
      
      {showBackground && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <StandardSelect
            id="background"
            label="Background Style"
            icon={<Mountain size={16} />}
            colorVariant="green"
            value={selectedBackground || ''}
            isMulti={false}
            onChange={handleBackgroundChange}
            options={[
              { value: 'none', label: 'None' },
              ...mapToSelectOptions(backgroundOptions)
            ]}
            placeholder="Background"
            className={styles.vibrantInput}
          />
        </motion.div>
      )}
    </div>
  );
};
